// Utilities
import 'babel-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { BreakpointProvider, setDefaultBreakpoints } from 'react-socks';

import { client } from './apolloSetup.js';
import { unregister } from './registerServiceWorker.js';
import {
  ROOT_QUERY,
  GET_ORG_ID,
  GET_STORE_ID,
  CHECK_CART_ID,
  CART_STATUS_QUERY,
  GET_CART,
} from './constants/queries.js';
import Storefront from './components/Storefront.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import { clearUtms } from './helpers/cookieHelpers.js';

// bootstrap imports
import 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'jquery';
import $ from 'jquery';

window.jQuery = window.$ = $;

// set component breakpoints
setDefaultBreakpoints([
  { xsmall: 0 }, // all mobile devices
  { small: 576 }, // mobile devices (not sure which one's this big)
  { medium: 768 }, // ipad, ipad pro, ipad mini, etc
  { large: 992 }, // smaller laptops
  { xlarge: 1200 }, // laptops and desktops
]);

// client.domain.com/storefront
// --> domain routes to apollo, checks path for storefront slug, and proxies request to the storefront app
// --> send root query with null org revv_uid, it will check the domain and return the org's settings

// store.revv.co/rv_org_uid/storefront
// --> read the org's revv_uid from the url
// --> send root query with org revv_uid, it will return the org's settings

const findId = RegExp(/\/(rv_org_\w+)\//);
const pathName = window.location.pathname;
const found = findId.exec(pathName);
let orgId = found ? found[1] : null;
let basename = orgId ? `/${orgId}/storefront` : '/storefront';

// find product slug if it exists
const findSlug = RegExp(/storefront\/(.*)\/details/);
const foundSlug = findSlug.exec(pathName);
const productSlug = foundSlug ? foundSlug[1] : '';

client
  .query({ query: ROOT_QUERY, variables: { revvUid: orgId }, fetchPolicy: 'cache-first' })
  .then(async ({ data }) => {
    if (data.organization) {
      client.writeQuery({
        query: GET_ORG_ID,
        data: { orgId: data.organization.revv_uid },
      });

      // get org id from query if wer didnt get from url
      orgId = orgId || data.organization.revv_uid;

      client.writeQuery({
        query: GET_STORE_ID,
        data: { storeId: data.organization.store.id, revv_uid: data.organization.store.revv_uid },
      });

      // check cartId & cart, if it's closed, create a new one
      const cartId = client.readQuery({ query: CHECK_CART_ID })?.cartId;
      if (cartId) {
        client
          .query({
            query: CART_STATUS_QUERY,
            fetchPolicy: 'network-only',
            variables: { orgId: data.organization.revv_uid, cartId: cartId },
          })
          .then(({ data }) => {
            if (!data.organization.store.cart || data.organization.store.cart.status === 'closed') {
              clearUtms();
              client.writeQuery({ query: CHECK_CART_ID, data: { cartId: null } });
              client.writeQuery({ query: GET_CART, data: { cart: null } });
            }
          });
      }

      if (data.organization.win_red_store && data.organization.win_red_store_slug) {
        basename = '/' + data.organization.win_red_store_slug + basename;
      }

      ReactDOM.render(
        <ApolloProvider client={client}>
          <BreakpointProvider>
            <Router basename={basename}>
              <ScrollToTop>
                <Route
                  path="/"
                  render={props => (
                    <Storefront
                      settings={data.organization.store.settings}
                      collections={data.organization.store.collections}
                      cartId={cartId}
                      orgId={orgId}
                      data={data}
                      productSlug={productSlug}
                      storeId={data.organization.store.id}
                      {...props}
                    />
                  )}
                />
              </ScrollToTop>
            </Router>
          </BreakpointProvider>
        </ApolloProvider>,
        document.getElementById('root')
      );
    } else {
      console.log('Organization not found.');
    }
  });

// currently causing issues in Chrome
unregister();
// sourceMap for HoneyBadger
//# sourceMappingURL=/static/js/main.<hash>.js.map
